import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc,
  getDocs,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  runTransaction,
  limit,
  setDoc,
  onSnapshot,
  type Unsubscribe
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { ADMIN_USER_EMAILS } from '../constants';
import type { CreditWallet, CreditTransaction, CreditCost, PaymentIntent } from '../src/types';

const walletsCollection = collection(db, 'wallets');
const transactionsCollection = collection(db, 'credit_transactions');
const costsCollection = collection(db, 'credit_costs');
const paymentIntentsCollection = collection(db, 'payment_intents');

// Default credit costs for actions
const DEFAULT_CREDIT_COSTS = {
  'essay_marking': 1,
  'essay_remarking': 1,
  'transcription': 1,
  'plagiarism_check': 1
};

// Error handling helper
const handleCreditError = (error: any, context: string) => {
  console.error(`Credit service error during ${context}:`, error);
  throw new Error(`Credit system error: ${error?.message || 'Unknown error occurred'}`);
};

// Check if user is admin (unlimited credits)
export const isAdminUser = (userEmail?: string | null): boolean => {
  return !!(userEmail && ADMIN_USER_EMAILS.includes(userEmail));
};

// Initialize credit costs in database (run once during setup)
export const initializeCreditCosts = async (): Promise<void> => {
  try {
    for (const [actionType, creditsCost] of Object.entries(DEFAULT_CREDIT_COSTS)) {
      // Check if cost already exists
      const q = query(
        costsCollection,
        where('actionType', '==', actionType),
        where('activeTo', '==', null),
        limit(1)
      );
      const existing = await getDocs(q);
      
      if (existing.empty) {
        await addDoc(costsCollection, {
          actionType,
          creditsCost,
          version: 1,
          activeFrom: serverTimestamp(),
          activeTo: null
        });
      }
    }
  } catch (error) {
    handleCreditError(error, 'initializing credit costs');
  }
};

// Get or create wallet for user
export const getOrCreateWallet = async (userId: string, userEmail?: string): Promise<CreditWallet> => {
  try {
    // Use the user's ID as the document ID for direct, secure access.
    const walletRef = doc(db, 'wallets', userId);
    const walletSnap = await getDoc(walletRef);

    if (walletSnap.exists()) {
      return { id: walletSnap.id, ...walletSnap.data() } as CreditWallet;
    }
    
    // If the wallet doesn't exist, create it.
    const newWalletData = {
      ownerType: 'user' as const,
      ownerId: userId,
      balance: 0,
      status: 'active' as const,
      lifetimeSpend: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(walletRef, newWalletData); // Use setDoc to create a document with a specific ID.
    
    // We need to return a non-server-timestamp version for immediate use.
    const newWalletForClient = { ...newWalletData, createdAt: new Date(), updatedAt: new Date() };
    return { id: userId, ...newWalletForClient } as CreditWallet;
    
  } catch (error) {
    handleCreditError(error, 'getting or creating wallet');
    throw error;
  }
};

// Get current credit balance
export const getCreditBalance = async (userId: string, userEmail?: string): Promise<number> => {
  try {
    // Admin users have unlimited credits
    if (isAdminUser(userEmail)) {
      return 999999; // Return a very high number for display
    }
    
    const wallet = await getOrCreateWallet(userId, userEmail);
    return wallet.balance;
  } catch (error) {
    handleCreditError(error, 'getting credit balance');
    return 0;
  }
};

// Get credit cost for an action
export const getCreditCost = async (actionType: string): Promise<number> => {
  try {
    const q = query(
      costsCollection,
      where('actionType', '==', actionType),
      where('activeTo', '==', null),
      orderBy('activeFrom', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return snapshot.docs[0].data().creditsCost;
    }
    
    // Return default cost if not found
    return DEFAULT_CREDIT_COSTS[actionType as keyof typeof DEFAULT_CREDIT_COSTS] || 1;
  } catch (error) {
    handleCreditError(error, 'getting credit cost');
    return 1; // Fallback cost
  }
};

// Check if user has sufficient credits
export const hasSufficientCredits = async (
  userId: string, 
  requiredCredits: number,
  userEmail?: string
): Promise<boolean> => {
  try {
    // Admin users always have sufficient credits
    if (isAdminUser(userEmail)) {
      return true;
    }
    
    const balance = await getCreditBalance(userId, userEmail);
    return balance >= requiredCredits;
  } catch (error) {
    handleCreditError(error, 'checking sufficient credits');
    return false;
  }
};

// Consume credits atomically
export const consumeCredits = async (
  userId: string,
  amount: number,
  relatedObjectType?: string,
  relatedObjectId?: string,
  memo?: string,
  userEmail?: string
): Promise<CreditTransaction> => {
  try {
    // Admin users don't consume credits
    if (isAdminUser(userEmail)) {
      // Create a mock transaction for audit purposes
      return {
        id: 'admin_mock_' + Date.now(),
        walletId: 'admin_wallet',
        type: 'consume',
        amount: 0, // Admin consumes 0 credits
        balanceAfter: 999999,
        currency: 'credits',
        relatedObjectType,
        relatedObjectId,
        memo: `Admin user - no credits consumed. Original amount: ${amount}`,
        actorId: userId,
        actorType: 'user',
        createdAt: new Date()
      } as CreditTransaction;
    }

    const functions = getFunctions();
    const consumeFn = httpsCallable(functions, 'consumeCredits');
    const response: any = await consumeFn({
      amount,
      relatedObjectType,
      relatedObjectId,
      memo
    });

    const transaction = response?.data?.transaction;
    if (!transaction?.id) {
      throw new Error('Invalid response from consumeCredits function.');
    }

    return {
      ...transaction,
      createdAt: transaction.createdAt || new Date()
    } as CreditTransaction;
  } catch (error) {
    handleCreditError(error, 'consuming credits');
    throw error;
  }
};

// Refund credits (for failed operations)
export const refundCredits = async (
  userId: string,
  amount: number,
  relatedObjectType?: string,
  relatedObjectId?: string,
  memo?: string,
  userEmail?: string
): Promise<CreditTransaction> => {
  try {
    // Admin users don't need refunds
    if (isAdminUser(userEmail)) {
      return {
        id: 'admin_mock_refund_' + Date.now(),
        walletId: 'admin_wallet',
        type: 'refund',
        amount: 0,
        balanceAfter: 999999,
        currency: 'credits',
        relatedObjectType,
        relatedObjectId,
        memo: `Admin user - no refund needed. Original amount: ${amount}`,
        actorId: userId,
        actorType: 'user',
        createdAt: new Date()
      } as CreditTransaction;
    }
    
    const functions = getFunctions();
    const refundFn = httpsCallable(functions, 'refundCredits');
    const response: any = await refundFn({
      amount,
      relatedObjectType,
      relatedObjectId,
      memo
    });

    const transaction = response?.data?.transaction;
    if (!transaction?.id) {
      throw new Error('Invalid response from refundCredits function.');
    }

    return {
      ...transaction,
      createdAt: transaction.createdAt || new Date()
    } as CreditTransaction;
  } catch (error) {
    handleCreditError(error, 'refunding credits');
    throw error;
  }
};

// Add credits (for purchases)
export const addCredits = async (
  userId: string,
  amount: number,
  relatedObjectType?: string,
  relatedObjectId?: string,
  memo?: string
): Promise<CreditTransaction> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Get or create wallet directly by user ID
      const walletRef = doc(db, 'wallets', userId);
      const walletDoc = await transaction.get(walletRef);
      
      let walletData;

      if (!walletDoc.exists()) {
        // Create new wallet if it doesn't exist
        const newWalletData = {
          ownerType: 'user' as const,
          ownerId: userId,
          balance: 0,
          status: 'active' as const,
          lifetimeSpend: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        transaction.set(walletRef, newWalletData);
        walletData = { ...newWalletData, balance: 0 }; // Use 0 balance for calculation
      } else {
        walletData = walletDoc.data() as CreditWallet;
      }
      
      // Calculate new balance
      const newBalance = walletData.balance + amount;
      
      // Update wallet
      transaction.update(walletRef, {
        balance: newBalance,
        updatedAt: serverTimestamp()
      });
      
      // Create transaction record
      const transactionData = {
        walletId: walletRef.id,
        type: 'purchase',
        amount: amount, // Positive for purchase
        balanceAfter: newBalance,
        currency: 'credits',
        relatedObjectType,
        relatedObjectId,
        memo,
        actorId: userId,
        actorType: 'user',
        createdAt: serverTimestamp()
      };
      
      const transactionRef = await addDoc(transactionsCollection, transactionData);
      
      return {
        id: transactionRef.id,
        ...transactionData,
        createdAt: new Date()
      } as CreditTransaction;
    });
  } catch (error) {
    handleCreditError(error, 'adding credits');
    throw error;
  }
};

// Get transaction history
export const getTransactionHistory = async (
  userId: string,
  limitCount: number = 50
): Promise<CreditTransaction[]> => {
  try {
    // Querying directly on 'actorId' which is the userId.
    // This is more efficient and allows for simpler, more secure Firestore rules.
    const q = query(
      transactionsCollection,
      where('actorId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CreditTransaction));
  } catch (error) {
    handleCreditError(error, 'getting transaction history');
    return [];
  }
};

// Admin functions for cost management
export const updateCreditCost = async (actionType: string, newCost: number): Promise<void> => {
  try {
    // First, deactivate existing cost
    const existingQuery = query(
      costsCollection,
      where('actionType', '==', actionType),
      where('activeTo', '==', null)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    
    // Deactivate existing costs
    for (const doc of existingSnapshot.docs) {
      await updateDoc(doc.ref, {
        activeTo: serverTimestamp()
      });
    }
    
    // Create new cost entry
    await addDoc(costsCollection, {
      actionType,
      creditsCost: newCost,
      version: existingSnapshot.docs.length + 1,
      activeFrom: serverTimestamp(),
      activeTo: null
    });
  } catch (error) {
    handleCreditError(error, 'updating credit cost');
  }
};

// Get all current credit costs
export const getAllCreditCosts = async (): Promise<CreditCost[]> => {
  try {
    const q = query(
      costsCollection,
      where('activeTo', '==', null),
      orderBy('actionType')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CreditCost));
  } catch (error) {
    handleCreditError(error, 'getting all credit costs');
    return [];
  }
};

// Estimate credits needed for essay marking job
export const estimateCreditsForJob = async (
  actionType: string = 'essay_marking',
  modelCount: number = 1
): Promise<number> => {
  try {
    const costPerModel = await getCreditCost(actionType);
    return costPerModel * modelCount;
  } catch (error) {
    handleCreditError(error, 'estimating credits for job');
    return 1; // Fallback estimate
  }
};

// Real-time credit balance listener
export const subscribeToCreditBalance = (
  userId: string,
  userEmail: string | undefined,
  onBalanceUpdate: (balance: number) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    // Admin users have unlimited credits
    if (isAdminUser(userEmail)) {
      onBalanceUpdate(999999);
      return () => {}; // No-op unsubscribe for admin users
    }

    const walletRef = doc(db, 'wallets', userId);
    
    return onSnapshot(
      walletRef,
      (doc) => {
        try {
          if (doc.exists()) {
            const walletData = doc.data() as CreditWallet;
            onBalanceUpdate(walletData.balance || 0);
          } else {
            // Wallet doesn't exist yet, start with 0 balance
            onBalanceUpdate(0);
          }
        } catch (error) {
          console.error('Error processing balance update:', error);
          onError?.(error as Error);
        }
      },
      (error) => {
        console.error('Credit balance listener error:', error);
        onError?.(error as Error);
      }
    );
  } catch (error) {
    console.error('Error setting up credit balance listener:', error);
    onError?.(error as Error);
    return () => {}; // Return no-op unsubscribe
  }
};

// Real-time transaction history listener
export const subscribeToTransactionHistory = (
  userId: string,
  limitCount: number = 10,
  onTransactionsUpdate: (transactions: CreditTransaction[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const q = query(
      transactionsCollection,
      where('actorId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    return onSnapshot(
      q,
      (snapshot) => {
        try {
          const transactions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as CreditTransaction));
          onTransactionsUpdate(transactions);
        } catch (error) {
          console.error('Error processing transaction history update:', error);
          onError?.(error as Error);
        }
      },
      (error) => {
        console.error('Transaction history listener error:', error);
        onError?.(error as Error);
      }
    );
  } catch (error) {
    console.error('Error setting up transaction history listener:', error);
    onError?.(error as Error);
    return () => {}; // Return no-op unsubscribe
  }
};
